/** Powered by @ttoss/config. https://ttoss.dev/docs/modules/packages/config/ */

// src/index.ts
import { useResponsiveValue, useBreakpointIndex } from "@theme-ui/match-media";

// src/theme/ThemeProvider.tsx
import * as React2 from "react";

// ../theme/dist/esm/index.js
var BruttalTheme = {
  colors: {
    text: "#0D0D0D",
    background: "#fff",
    backgroundBrand: "#C2C2C2",
    primary: "#0D0D0D",
    secondary: "#639",
    gray: "#555",
    muted: "#f6f6f6",
    danger: "red",
    accent: "#0000FF",
    textOnSurface: "#FFFFFF"
  },
  space: [0, 4, 8, 16, 32, 64, 128, 256, 512, 1024],
  fonts: {
    body: '"Inconsolata", sans-serif',
    heading: '"Work Sans", sans-serif',
    monospace: '"Inconsolata", sans-serif'
  },
  fontSizes: {
    xxs: "0.5rem",
    xs: "0.75rem",
    sm: "0.875rem",
    base: "1rem",
    lg: "1.125rem",
    xl: "1.25rem",
    "2xl": "1.5rem",
    "3xl": "1.875rem",
    "4xl": "2.25rem",
    "5xl": "3rem",
    "6xl": "3.75rem",
    "7xl": "4.5rem",
    "8xl": "6rem",
    "9xl": "8rem"
  },
  fontWeights: {
    thin: 100,
    extralight: 200,
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800
  },
  letterSpacings: {
    tighter: "-0.05em",
    tight: "-0.025em",
    normal: "0em",
    wide: "0.025em",
    wider: "0.05em",
    widest: "0.1em"
  },
  styles: {
    root: {
      fontFamily: "body",
      fontWeight: "normal"
    },
    a: {
      color: "primary",
      textDecoration: "underline",
      cursor: "pointer",
      fontFamily: "body"
    },
    progress: {
      color: "primary",
      backgroundColor: "background"
    }
  },
  borders: [0, "3px solid #0D0D0D"],
  buttons: {
    cta: {
      color: "white",
      backgroundColor: "primary"
    },
    muted: {
      color: "text",
      backgroundColor: "muted"
    },
    danger: {
      color: "white",
      backgroundColor: "danger"
    },
    primary: {
      ":disabled": {
        backgroundColor: "backgroundBrand",
        cursor: "default"
      }
    },
    secondary: {
      color: "text",
      backgroundColor: "white",
      borderColor: "text",
      borderWidth: 1,
      borderStyle: "solid",
      ":disabled": {
        opacity: 0.5,
        cursor: "default"
      }
    }
  },
  cards: {
    primary: {
      backgroundColor: "background",
      border: "1px solid black",
      padding: [4, 5],
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "fit-content"
    }
  },
  forms: {
    input: {
      fontFamily: "body"
    }
  },
  text: {
    default: {
      color: "text",
      fontFamily: "body"
    },
    title: {
      fontFamily: "heading",
      fontSize: 4,
      lineSpace: "3.5"
    },
    h1: {
      fontFamily: "heading",
      fontSize: "6xl",
      fontWeight: "bold",
      letterSpacing: "normal"
    },
    h2: {
      fontFamily: "heading",
      fontSize: "5xl",
      fontWeight: "bold",
      letterSpacing: "normal"
    },
    h3: {
      fontFamily: "heading",
      fontSize: "4xl",
      fontWeight: "bold",
      letterSpacing: "normal"
    },
    h4: {
      fontFamily: "heading",
      fontSize: "2xl",
      fontWeight: "bold",
      letterSpacing: "normal"
    },
    h5: {
      fontFamily: "heading",
      fontSize: "xl",
      fontWeight: "bold",
      letterSpacing: "normal"
    }
  }
};
var BruttalFonts = [
  "https://fonts.googleapis.com/css?family=Inconsolata:wght@100;200;300;400;500;700;800;900",
  "https://fonts.googleapis.com/css?family=Work+Sans:wght@100;200;300;400;500;700;800;900"
];

// src/theme/ThemeProvider.tsx
import { Global, css } from "@emotion/react";
import { ThemeProvider as ThemeUiProvider, merge } from "theme-ui";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var ThemeProvider = ({
  children,
  theme = {},
  fonts = BruttalFonts
}) => {
  const mergedTheme = React2.useMemo(() => {
    if (typeof theme === "function") {
      return theme;
    }
    return merge(BruttalTheme, theme);
  }, [theme]);
  return /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsxs(ThemeUiProvider, { theme: mergedTheme, children: [
    fonts.map((url) => {
      return /* @__PURE__ */ jsx(
        Global,
        {
          styles: css`
                @import url('${url}');
              `
        },
        url
      );
    }),
    children
  ] }) });
};
var ThemeProvider_default = ThemeProvider;

// src/theme/useTheme.ts
import { useThemeUI } from "theme-ui";
var useTheme = useThemeUI;

// src/components/Box.tsx
import { Box } from "theme-ui";

// src/components/Button.tsx
import { Button as ButtonUi } from "theme-ui";
import { jsx as jsx2 } from "react/jsx-runtime";
var Button = (props) => {
  return /* @__PURE__ */ jsx2(
    ButtonUi,
    {
      ...props,
      sx: { cursor: "pointer", fontFamily: "body", ...props.sx }
    }
  );
};

// src/components/Card.tsx
import { Card } from "theme-ui";

// src/components/Divider.tsx
import { Divider } from "theme-ui";

// src/components/Flex.tsx
import { Flex } from "theme-ui";

// src/components/Grid.tsx
import { Grid } from "theme-ui";

// src/components/Heading.tsx
import { Heading } from "theme-ui";

// src/components/Image.tsx
import { Image } from "theme-ui";

// src/components/Input.tsx
import { Input } from "theme-ui";

// src/components/Label.tsx
import { Label } from "theme-ui";

// src/components/Link.tsx
import { Link } from "theme-ui";

// src/components/LinearProgress.tsx
import {
  Progress
} from "theme-ui";

// src/components/Text.tsx
import { Text } from "theme-ui";

// src/components/Select.tsx
import { Select } from "theme-ui";

// src/components/Spinner.tsx
import { Spinner } from "theme-ui";

// src/components/Radio.tsx
import { Radio } from "theme-ui";

// src/components/Icon.tsx
import { Icon as IconUI } from "@iconify/react";
import { jsx as jsx3 } from "react/jsx-runtime";
var Icon = ({ icon, sx, iconProps, ...props }) => {
  return /* @__PURE__ */ jsx3(Text, { sx, ...props, children: /* @__PURE__ */ jsx3(IconUI, { ...iconProps, icon }) });
};

// src/components/Slider.tsx
import { Slider } from "theme-ui";

// src/components/Checkbox.tsx
import { Checkbox } from "theme-ui";

// src/components/InfiniteLinearProgress.tsx
import * as React3 from "react";
import { jsx as jsx4 } from "react/jsx-runtime";
var MAX_PROGRESS = 100;
var InfiniteLinearProgress = () => {
  const [progress, setProgress] = React3.useState(0);
  React3.useEffect(() => {
    const timer = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress === MAX_PROGRESS) {
          return 0;
        }
        let diff = 0;
        if (oldProgress > 0.97 * MAX_PROGRESS) {
          diff -= 0.75 * MAX_PROGRESS;
        } else if (oldProgress > 0.6 * MAX_PROGRESS) {
          diff = Math.random() * (MAX_PROGRESS - oldProgress) * 0.1;
        } else {
          diff = Math.random() * (MAX_PROGRESS * 0.02);
        }
        return Math.min(oldProgress + diff, MAX_PROGRESS);
      });
    }, 100);
    return () => {
      clearInterval(timer);
    };
  }, []);
  return /* @__PURE__ */ jsx4(Progress, { max: MAX_PROGRESS, value: progress, role: "progressbar" });
};

// src/components/Textarea.tsx
import { Textarea } from "theme-ui";
export {
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  Flex,
  Grid,
  Heading,
  Icon,
  Image,
  InfiniteLinearProgress,
  Input,
  Label,
  Progress as LinearProgress,
  Link,
  Radio,
  Select,
  Slider,
  Spinner,
  Text,
  Textarea,
  ThemeProvider_default as ThemeProvider,
  useBreakpointIndex,
  useResponsiveValue,
  useTheme
};
